import { z } from 'zod';

import { amortizationResultSchema, type AmortizationResult } from '@/shared/amortization';

// Fio da Simulação (legado: services/Amortization/Amortization.tsx:6-23, services/creditProduct/types/
// creditProductReadModel.ts:52-179). Resposta de `POST /Amortization`: o `AmortizationReadModel` direto, sem
// envelope (`useCreateSimulation` lê `resp.data`, creditContext.tsx:362-381). Dinheiro em centavos, taxa em fração.

/** A resposta do `POST /Amortization` (`AmortizationReadModel`, creditNoteReadModel.ts:287-347), frouxa. */
export const simulationResultSchema = amortizationResultSchema;
export type SimulationResult = AmortizationResult;

const text = z.string().nullish();
const num = z.number().nullish();
const bool = z.boolean().nullish();

/**
 * O produto como a simulação lê (`CreditProductReadModel`, creditProductReadModel.ts:52-179), só os campos que
 * mudam o formulário: tipo de amortização, instrumento, averbadora, faixa de taxa, prazo máximo, isenção de IOF,
 * tarifas por parcela e convênio. O `GET /Product/{id}` vem pela porta do `credit-product`.
 */
export const simulationProductSchema = z.looseObject({
  id: z.string(),
  name: text,
  amortizationType: text,
  instrumentType: text,
  warrantyRegistrationOffice: text,
  categoryName: text,
  /** Fração (0.0199 = 1,99%). */
  minimumInterestRate: num,
  maximumInterestRate: num,
  maximumAmortizationPeriods: num,
  allowFinanceTaxExemption: bool,
  /** Centavos. */
  paymentFixedCosts: num,
  manualWarranty: bool,
  payrollAgreement: z.unknown().nullish(),
  /** Os convênios do produto (`payrollAgreements`, OperationTab.tsx:237-243): o dia de repasse pelo órgão da garantia. */
  payrollAgreements: z.array(z.unknown()).nullish(),
});
export type SimulationProduct = z.infer<typeof simulationProductSchema>;

/** Linha da busca de produto (`GET /Product`, creditProductContext.tsx:171-191): id e nome. */
export const simulationProductOptionSchema = z.looseObject({ id: z.string(), name: z.string() });
export type SimulationProductOption = z.infer<typeof simulationProductOptionSchema>;

/** Corpo do `POST /Amortization`: `QueryAmortizationCreateModel` (QueryAmortizationCreateModel.ts:3-9). */
export interface SimulationBody {
  readonly productId?: unknown;
  readonly legalPerson?: unknown;
  readonly personId?: unknown;
  readonly registrationNumber?: unknown;
  readonly amortization?: Readonly<Record<string, unknown>>;
}

/**
 * O convênio do produto (`PayrollAgreementReadModel`, só o que o primeiro pagamento lê): dia de repasse, meses até a
 * primeira parcela, dia de corte, averbadora e o código do órgão (`internalId`).
 */
export const productAgreementSchema = z.looseObject({
  internalId: text,
  warrantyRegistrationOffice: text,
  paymentTransferDay: num,
  monthsUntilFirstPayment: num,
  warrantyRegistrationCutoffDay: num,
});
export type ProductAgreement = z.infer<typeof productAgreementSchema>;

/** O convênio como o produto o traz, ou `null` (o `payrollAgreement ?? null` do legado). */
export function productAgreement(value: unknown): ProductAgreement | null {
  if (value === null || value === undefined) return null;
  const parsed = productAgreementSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Os convênios do produto que o fio entende. */
export function productAgreements(product: Pick<SimulationProduct, 'payrollAgreements'> | null | undefined): ProductAgreement[] {
  return (product?.payrollAgreements ?? []).flatMap((item) => {
    const parsed = productAgreementSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

/** `POST /DataprevEmployee/FirstPaymentDate` (`TFirstPaymentDateProps`, ZetraMarginQueryUpdateModel.ts:38-41). */
export const firstPaymentDateResponseSchema = z.looseObject({
  firstPaymentDate: text,
  discountStartPeriod: text,
});
